import {RESP} from '@/_metronic/helpers/network/network-consts'
import {FC, useEffect, useMemo, useState} from 'react'
import SVG from 'react-inlinesvg'
import {Link, useLocation, useParams} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {Outlet, initialFilterOutlet} from '../outlet/core/_models'
import {getOutletById, getOutlets} from '../outlet/core/_requests'
import BookingForm from './components/BookingForm'
import {FormStateProvider, useBookingFormState} from './core/FormStateProvider'
import {ModalProvider, useSelectTreatmentModal} from './core/_ModalProvider'
import {createOrder} from './core/_requests'
import {DropdownOutletOrder} from './components/DropdownOutletOrder'
import {initialFilterProvince} from '@/app/core/commons/province/_models'
import {getProvince} from '@/app/core/commons/province/_requests'
import clsx from 'clsx'

export const CITCOM_OUTLET = '35eeb8b6-894d-49f7-aa6f-d18cd5c71222'

// Outlet yang tutup tetap bisa muncul di response, jadi disaring di sini
const isClosedOutlet = (outlet: any) =>
  [outlet?.name, outlet?.status].some((value) =>
    typeof value === 'string' ? value.toLowerCase().includes('close') : false
  )

const OrderSelectOutletPage: FC = () => {
  const location = useLocation()
  const [region, setRegion] = useState<any>(undefined)
  const [outlet, setOutlet] = useState<any | undefined>(undefined)
  const {showSuccessModal} = useSelectTreatmentModal()
  const {setIsLoading} = useBookingFormState()

  const isBooking = useMemo(() => {
    return location?.pathname?.startsWith('/booking')
  }, [location])

  const loadRegion = async (q: string, page: number) => {
    try {
      const body = {
        ...initialFilterProvince,
        filter: {
          ...initialFilterProvince.filter,
          set_name: Boolean(q),
          name: q,
        },
        page,
        limit: 10,
      }
      const res = await getProvince(body)
      if (res?.response?.code === RESP.SUCCESS) {
        const options =
          res?.response?.data?.map((outlet) => ({
            label: outlet?.name,
            value: outlet?.provinsi_id,
          })) || []

        return {
          options,
          hasMore: page < (res?.response?.total_page || 1),
          additional: {
            page: page + 1,
          },
        }
      }

      return {
        options: [],
        hasMore: false,
      }
    } catch (error) {
      return {
        options: [],
        hasMore: false,
      }
    }
  }

  const loadOptionsRegion: any = async (q, _, additional) => {
    const {page} = additional!
    const {options: responseOptions, hasMore} = await loadRegion(q, page)

    return {
      options: responseOptions,
      hasMore,
      additional: {
        page: page + 1,
      },
    }
  }

  const loadOutlet = async (q: string, page: number) => {
    try {
      const body: typeof initialFilterOutlet = {
        ...initialFilterOutlet,
        filter: {
          ...initialFilterOutlet.filter,
          set_name: Boolean(q),
          name: q,
          set_region_id: Boolean(region?.value),
          region_id: region?.value,
          set_status: true,
          status: 'active',
        },
        page,
        limit: 10,
      }
      const res = await getOutlets(body)
      if (res?.response?.code === RESP.SUCCESS) {
        const availableOutlets = (res?.response?.data || []).filter((outlet) => !isClosedOutlet(outlet))

        let options = availableOutlets.map((outlet) => ({
          label: outlet?.name,
          value: outlet?.guid,
        }))

        if (isBooking) {
          options = availableOutlets
            .filter((outlet) => outlet.guid === CITCOM_OUTLET)
            .map((outlet) => ({
              label: outlet?.name,
              value: outlet?.guid,
            }))

          setOutlet(options?.[0])
        }

        return {
          options,
          hasMore: page < (res?.response?.total_page || 1),
          additional: {
            page: page + 1,
          },
        }
      }

      return {
        options: [],
        hasMore: false,
      }
    } catch (error) {
      return {
        options: [],
        hasMore: false,
      }
    }
  }

  const loadOptionsOutlet: any = async (q, _, additional) => {
    const {page} = additional!
    const {options: responseOptions, hasMore} = await loadOutlet(q, page)

    return {
      options: responseOptions,
      hasMore,
      additional: {
        page: page + 1,
      },
    }
  }

  const handleCreateOrder = async (body: any) => {
    try {
      const res = await createOrder(body)
      if (res?.response?.code === RESP.SUCCESS) {
        showSuccessModal()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className='container z-10'>
        <div className='row justify-content-center my-5 gap-5'>
          <div className='col-12 col-md-8 col-lg-6'>
            <div className='row'>
              <div className='col'>
                {!isBooking ? (
                  <img src={toAbsoluteUrl('/media/logos/OIC-brands.png')} className='img-fluid' />
                ) : (
                  <div className='d-flex justify-content-center'>
                    <img src={toAbsoluteUrl('/media/logos/OIC-brands.png')} className='img-fluid w-50' />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='col-12 col-md-8 col-lg-6'>
            <div className='card py-4 px-5 shadow-wit-2 bg-illustration-order'>
              <div className='text-center'>
                <h1 className='fw-bolder mt-4'>Booking Reservation</h1>
                {!isBooking && (
                  <>
                    <p className='text-gray-600 mt-8'>Pilih Region</p>
                    <DropdownOutletOrder
                      loadOptions={loadOptionsRegion}
                      value={region}
                      placeholder='Pilih Region'
                      isClearable
                      onChange={(newValue) => {
                        setRegion(newValue)
                        setOutlet(null)
                      }}
                    />
                  </>
                )}
                <p className='text-gray-600 mt-8'>Pilih Outlet</p>
                <DropdownOutletOrder
                  selectKey={JSON.stringify(region) || 'outlet'}
                  loadOptions={loadOptionsOutlet}
                  value={outlet}
                  isDisabled={isBooking}
                  placeholder='Pilih Outlet'
                  onChange={(newValue) => {
                    setOutlet(newValue)
                  }}
                />
              </div>
              <BookingForm outlet_id={outlet?.value || ''} handleSubmit={handleCreateOrder} />
              <Link
                to='/privacy-policy'
                className={clsx('w-100 text-center mt-3 fs-5 fw-bold', {
                  'text-info': !isBooking,
                })}
                style={{
                  color: isBooking ? '#ea0f00 !important' : 'inherit',
                }}
              >
                Privacy Policy
              </Link>
              <img
                src={toAbsoluteUrl('/media/illustrations/Ilustration_order.png')}
                className='d-none d-md-block w-200px img-fluid position-absolute backdrop-order bottom-0 z-9'
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const OrderSelectOutletWrapper: FC = () => {
  return (
    <>
      <FormStateProvider>
        <ModalProvider>
          <OrderSelectOutletPage />
        </ModalProvider>
      </FormStateProvider>
    </>
  )
}

export default OrderSelectOutletWrapper
